/**
 * @fileoverview Connect a lead's recorded state to explicit staff decision and matter-handoff controls. Preview context comes from `kc-internal-frontend/src/components/Intake/intakeApi.js`; the backend owns accepted decisions and conversion eligibility. The parent owns refreshed lead state after submission. A suggested outcome or opened preview does not itself record a staff choice.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ToastContext from '../../../ToastContext';
import { AppModal } from '../../shared';
import AppIcon from '../../shared/AppIcon';
import { createIntakeDecision } from '../intakeApi';
import { TextArea, TextInput } from '../IntakeDetailPrimitives';

const DECISION_ACTIONS = [
  { decision: 'accepted', label: 'Accept & Convert', submitLabel: 'Accept and Convert', icon: 'pi pi-check', tone: 'primary' },
  { decision: 'archived', label: 'Reject & Archive', submitLabel: 'Reject & Archive', icon: 'pi pi-folder', tone: 'danger' }];
const PIPEDRIVE_HANDOFF_MODES = new Set([
  'engagement_letter_handoff',
  'pipedrive_won_handoff',
  'pipedrive_evaluation_documents_handoff'
]);



function isPipedriveHandoffMode(mode) {
  return PIPEDRIVE_HANDOFF_MODES.has(mode);
}



function canRecordLeadDecision(lead, convertedMatter = null) {
  if (convertedMatter) return false;
  if (['converted', 'engagement_letter_to_be_sent', 'rejected', 'duplicate', 'archived'].includes(lead.status)) return false;
  if (['rejected', 'duplicate', 'archived'].includes(lead.final_decision)) return false;
  return true;
}



export function DecisionActionButtons({ lead, onOpenDecision, previewMatter = null, conversionBlocker = '', compact = false, engagementLetterHandoff = false }) {
  const convertedMatter = lead.converted_matter_number;
  const canRecordDecision = canRecordLeadDecision(lead, convertedMatter);
  return (
    <div className={`intake-decision-actions${compact ? ' intake-decision-actions--compact' : ''}`}>
      {DECISION_ACTIONS.map(

      (action) => {
        const isAccept = action.decision === 'accepted';
        const className = [
          'intake-button',
          ['primary', 'danger'].includes(action.tone) ? 'intake-detail-convert-button' : '',
          action.tone === 'primary' ? 'intake-button--primary' : '',
          action.tone === 'danger' ? 'intake-button--danger' : '',
          compact ? 'intake-decision-actions__button--compact' : ''
        ].filter(Boolean).join(' ');
        const label = convertedMatter && isAccept ?
        `Lead converted as matter ${convertedMatter}` :
        isAccept && engagementLetterHandoff ? 'Accept case and queue Pipedrive handoff' :
        isAccept && previewMatter ? `Accept and convert lead to client as matter ${previewMatter}` : action.label;
        const buttonText = isAccept && engagementLetterHandoff ? 'Accept Case' : action.label;
        return (
          <button
            type="button"
            className={className}
            disabled={!canRecordDecision}
            key={action.decision}
            onClick={() => onOpenDecision(action.decision)}
            title={isAccept && conversionBlocker ? conversionBlocker : action.label}
            aria-label={label}>
            <AppIcon icon={action.icon} aria-hidden="true" />
            {isAccept && previewMatter && !compact && !engagementLetterHandoff ?
            <span className="intake-detail-convert-label">
              <span className="intake-detail-convert-action">{action.label}</span>
              <span className="intake-detail-convert-matter">{previewMatter}</span>
            </span> :
            <span>{buttonText}</span>}
          </button>);
      })}
    </div>);
}

const DECISION_MODAL_COPY = {
  accepted: {
    title: 'Accept & Convert',
    description: 'Confirm the generated matter number before creating the client matter.',
    heading: 'Conversion Details',
    submitLabel: 'Accept and Convert',
    savingLabel: 'Converting',
    successSummary: 'Converted',
    successDetail: 'Lead accepted and converted',
    icon: 'pi pi-check',
    buttonClass: 'intake-button--primary intake-detail-convert-button'
  },
  archived: {
    title: 'Reject & Archive',
    description: 'Record why this intake lead should be rejected and archived.',
    heading: 'Reject & Archive Details',
    submitLabel: 'Reject & Archive',
    savingLabel: 'Archiving',
    successSummary: 'Archived',
    successDetail: 'Lead rejected and archived',
    icon: 'pi pi-folder',
    buttonClass: 'intake-button--danger intake-detail-convert-button'
  }
};



export function IntakeDecisionModal({ lead, visible, initialPreview = null, initialDecision = 'accepted', onClose, onLeadUpdated, onDecisionRecorded = () => {} }) {
  const showToast = useContext(ToastContext);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [savingDecision, setSavingDecision] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const decision = DECISION_MODAL_COPY[initialDecision] ? initialDecision : 'accepted';
  const copy = DECISION_MODAL_COPY[decision];
  const isAcceptDecision = decision === 'accepted';

  const loadDecisionData = useCallback(

  async () => {
    if (!isAcceptDecision) {
      setLoaded(true);
      return;
    }
    setPreview(initialPreview);
    setLoaded(true);
    setLoading(false);
  }, [initialPreview, isAcceptDecision]);

  useEffect(() => {
    if (visible && !loaded) loadDecisionData();
  }, [visible, loaded, loadDecisionData]);

  useEffect(

  () => {
    setPreview(initialPreview);
    setLoaded(false);
    setReason('');
    setNotes('');
    setSavingDecision('');
  }, [decision, initialPreview, lead.id]);

  const submitDecision =

  async () => {
    setSavingDecision(decision);
    try {
      const response = await createIntakeDecision(lead.id, {
        decision,
        convert: isAcceptDecision,
        draft_rejection_email: !isAcceptDecision,
        reason,
        notes
      });
      if (response.data.lead) onLeadUpdated(response.data.lead);
      const conversion = response.data.conversion || {};
      if (isAcceptDecision && isPipedriveHandoffMode(conversion.mode)) {
        setPreview((current) => ({
          ...(current || {}),
          can_convert: false
        }));
      } else if (isAcceptDecision && conversion.matter_number) {
        setPreview((current) => ({
          ...(current || {}),
          converted_matter_number: conversion.matter_number,
          matter_number: conversion.matter_number,
          can_convert: false
        }));
      }
      const pipedriveEvaluationHandoff = conversion.mode === 'pipedrive_evaluation_documents_handoff';
      const engagementLetterHandoff = conversion.mode === 'engagement_letter_handoff';
      showToast({
        severity: 'success',
        summary: isPipedriveHandoffMode(conversion.mode) ? 'Pipedrive handoff' : copy.successSummary,
        detail: pipedriveEvaluationHandoff ?
          'Assigned the Pipedrive deal and document-review activity to Kimberly' :
          engagementLetterHandoff ? 'Marked engagement letter to be sent; waiting for Pipedrive to be marked won' : copy.successDetail
      });
      if (!isAcceptDecision) {
        onClose?.();
        onDecisionRecorded(response.data);
      }
    } catch (error) {
      console.error('Failed to record intake decision:', error);
      const nextPreview = error?.response?.data?.preview;
      if (nextPreview) setPreview(nextPreview);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to record decision' });
    } finally {
      setSavingDecision('');
    }
  };

  const convertedMatter = lead.converted_matter_number || preview?.converted_matter_number;
  const usesEngagementLetterHandoff = isAcceptDecision && preview?.conversion_mode === 'engagement_letter_handoff';
  const usesEvaluationDocumentsHandoff = isAcceptDecision && preview?.conversion_mode === 'pipedrive_evaluation_documents_handoff';
  const usesPipedriveHandoff = isAcceptDecision && isPipedriveHandoffMode(preview?.conversion_mode);
  const conversionBlocked = Boolean(
    convertedMatter ||
    (!usesPipedriveHandoff && preview?.exists) ||
    preview?.missing_fields?.length ||
    preview?.can_convert === false
  );
  const submitDisabled = Boolean(savingDecision) || loading || (isAcceptDecision && (!preview || conversionBlocked));
  const submitButton = (
    <button type="button" className={`intake-button ${copy.buttonClass} intake-decision-modal__submit`} disabled={submitDisabled} onClick={submitDecision}>
            <AppIcon icon={copy.icon} aria-hidden="true" />
            <span>{savingDecision === decision ? copy.savingLabel : copy.submitLabel}</span>
        </button>);

  return (
    <AppModal
      visible={visible}
      title={copy.title}
      description={usesEvaluationDocumentsHandoff ? 'Confirm creating or updating the Pipedrive deal, assigning it to Kimberly, and adding a document-review activity in Evaluation of Documents.' : usesEngagementLetterHandoff ? 'Confirm moving the linked Pipedrive deal to engagement letter to be sent. The won webhook will create and link the matter after Pipedrive is marked won.' : copy.description}
      onClose={onClose}
      footer={submitButton}
      width="min(640px, calc(100vw - 2rem))"
      className="intake-decision-modal">

            <div className="intake-stack">
            <section className="intake-decision-panel">
                <div className="intake-section-heading">
                    <h2>{copy.heading}</h2>
                    {loading && <span>Loading</span>}
                </div>
                <div className={`intake-decision-grid ${isAcceptDecision ? '' : 'intake-decision-grid--single'}`}>
                    {isAcceptDecision &&
                    <div className="intake-decision-preview">
                        <span>{usesPipedriveHandoff ? 'Pipedrive Deal' : 'Generated Matter'}</span>
                        <strong>{usesPipedriveHandoff ? preview?.pipedrive_deal_id ? `Deal ${preview.pipedrive_deal_id}` : '-' : convertedMatter || preview?.matter_number || '-'}</strong>
                        {convertedMatter && <Link to={`/case_details/${convertedMatter}`}>Open matter</Link>}
                        {usesEngagementLetterHandoff && !convertedMatter && <small>Matter will be created after Pipedrive marks the deal won.</small>}
                        {usesEvaluationDocumentsHandoff && !convertedMatter && <small>Deal and activity will be assigned to Kimberly.</small>}
                        {!usesPipedriveHandoff && !convertedMatter && preview?.exists && <small className="intake-danger-text">Matter number already exists</small>}
                        {!convertedMatter && preview?.missing_fields?.length > 0 &&
            <small className="intake-danger-text">Missing {preview.missing_fields.join(', ')}</small>}

                    </div>
                    }
                    <TextInput label="Reason" value={reason} onChange={setReason} />
                    <TextArea label="Notes" value={notes} onChange={setNotes} rows={3} />
                </div>
            </section>
        </div>
        </AppModal>);
}
